import type { VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '../cn.js';
import type { Icon } from '../icons.js';
import { buttonVariants } from './button.js';

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof buttonVariants> {
  /** The icon to draw. It is decorative: the accessible name comes from `label`. */
  icon: Icon;
  /**
   * What the button does, in words. Required: an icon alone is not a name, and every icon-only
   * control must announce itself.
   */
  label: string;
}

const squareBySize = {
  sm: 'w-(--button-height-sm) p-0',
  md: 'w-(--button-height-md) p-0',
  lg: 'w-(--button-height-lg) p-0',
} as const;

/** A button whose whole content is one icon, with the label as its accessible name. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon: IconComponent,
    label,
    className,
    variant = 'ghost',
    size = 'md',
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      /*
       * **No `title`.** It used to carry one as well as the label, which gave every icon button
       * the browser's own tooltip — so one wrapped in `Tooltip` showed two, ours and the
       * operating system's, and some screen readers announced the name twice. `aria-label` is the
       * accessible name; a visible hint is what `Tooltip` is for.
       */
      className={cn(buttonVariants({ variant, size }), squareBySize[size ?? 'md'], className)}
      {...props}
    >
      <IconComponent aria-hidden="true" className="size-(--size-icon-md)" />
    </button>
  );
});
